import * as faceapi from '@vladmandic/face-api';
import FJCoreConfig from '../../core/FJCoreConfig.js';

function getDefaultMinConfidence() {
    const c = FJCoreConfig.FACE_DETECT_MIN_CONFIDENCE;
    return typeof c === 'number' && c > 0 && c < 1 ? c : 0.65;
}
// 人脸检测：最大人脸数
const FACE_DETECT_MAX_RESULTS = 4;
// 说话者判定阈值（嘴部开合度等）
// const SPEAKER_STANDARD_SCORE = 0.18;

/**
 * 检测单帧：人脸检测、说话检测、主体分析、裁切规则
 * 输入：视频帧或画布 + 音频能量
 * 输出：检测结果数据（不含绘制）
 * @param {HTMLCanvasElement} canvas - 已绘制画面的画布（当前实现仅接受 `HTMLCanvasElement`）
 * @param {Object} [options] - 检测选项
 * @param {number} [options.contrast] - 类型：`number`（对比度倍数，`1` 不变，`>1` 拉大亮暗差）。**默认：本文件未读取**。对准确度：**适度提高对比**有时能减轻欠曝/灰蒙蒙带来的漏检；**提得过大**易丢细节、高光/暗部死黑，反增误检或漏检。效果强依赖光照与相机，需实测。**当前未使用**，对结果无影响。
 * @param {number} [options.minConfidence] - 类型：`number`，取值约 **0～1**（置信度）。**默认：见 `FJCoreConfig.FACE_DETECT_MIN_CONFIDENCE`**。越高越严、漏检略增；越低越松、误检略增。对速度：**小到中等**——主要仍是模型推理。
 * @param {number} [options.maxResults] - 类型：`number`（正整数，单帧最多人脸数）。**默认：`4`**（见 `FACE_DETECT_MAX_RESULTS`）。常见场景 1～2 人可设 `2`～`4`；多人同框再加大。对速度：**中等**。
 * @param {number} [options.speakerThreshold] - 类型：`number`（嘴部开合等归一化分数，越大越难判成在说话）。**默认：未启用**；若启用注释里的 `analyzeSpeaking`，历史上可参考 **`0.18`**（原 `SPEAKER_STANDARD_SCORE`）。当前对速度无影响；若接 landmarks，成本在 68 点而非该阈值。
 * @returns {Promise<DetectionResult>}
 */
export async function detectFrame(canvas, options = {}) {
    const isCanvas = canvas instanceof HTMLCanvasElement;

    if (!isCanvas) {
        throw new Error('detectFrame: input must be HTMLVideoElement or HTMLCanvasElement');
    }

    const vw = canvas.width;
    const vh = canvas.height;

    const detections = await detectFacesOnCanvas(canvas, options);

    if (detections.length === 0) {
        return [];
    }
    // 如果需要嘴唇开合度
    // if (0) {
    //     // 仅用于裁切计算，不需要说话检测的详细数据
    //     return analyzeSpeaking(detections, {
    //         speakerThreshold: options.speakerThreshold,
    //         width: vw,
    //         height: vh,
    //     });
    // }
    const facesData = [];
    for (let i = 0; i < detections.length; i++) {
        const detection = detections[i];
        const box = detection.box;
        facesData.push({
            x: box.x / vw,
            y: box.y / vh,
            width: box.width / vw,
            height: box.height / vh,
        });
    }

    return facesData;
}

/**
 * 在画布上执行人脸检测（SSD MobileNet，当前未跑 68 点）
 * @param {HTMLCanvasElement} canvas - 已绘制好画面的画布
 * @param {Object} [options] - 见 `detectFrame`
 * @param {number} [options.minConfidence] - 默认与 `FJCoreConfig.FACE_DETECT_MIN_CONFIDENCE` 一致，范围约 `0～1`
 * @param {number} [options.maxResults] - 默认 `4`，正整数
 * @returns {Promise<Array>} face-api 检测结果数组
 */
const detectFacesOnCanvas = async (canvas, options = {}) => {
    const minConfidence =
        typeof options.minConfidence === 'number' &&
        options.minConfidence > 0 &&
        options.minConfidence < 1
            ? options.minConfidence
            : getDefaultMinConfidence();
    const maxResults =
        typeof options.maxResults === 'number' &&
        Number.isInteger(options.maxResults) &&
        options.maxResults >= 1
            ? options.maxResults
            : FACE_DETECT_MAX_RESULTS;
    const detections = await faceapi.detectAllFaces(
        canvas,
        new faceapi.SsdMobilenetv1Options({
            minConfidence,
            maxResults,
        }),
    );
    // .withFaceLandmarks(); //如果需要嘴唇开合度，则需要启用
    return detections;
};
// /**
//  * 根据 68 点关键点计算嘴部开合度（上唇 51 / 下唇 57 距离，归一化到脸宽）
//  * @param {Object} landmarks - face-api 返回的 landmarks（含 positions）
//  * @returns {number} 开合度 [0, +)，用于说话者判定
//  */
// const calculateMouthOpenness = landmarks => {
//     if (!landmarks || !landmarks.positions) return 0;

//     const positions = landmarks.positions;
//     if (positions.length < 68) return 0;

//     const topLip = positions[51];
//     const bottomLip = positions[57];
//     const distance = Math.sqrt(Math.pow(topLip.x - bottomLip.x, 2) + Math.pow(topLip.y - bottomLip.y, 2));

//     const faceWidth = Math.abs(positions[16].x - positions[0].x);
//     if (faceWidth === 0) return 0;

//     return distance / faceWidth;
// };
/**
 * 根据人脸检测结果与当前帧音频能量，分析每人是否在说话及当前说话者
 * @param {Array} detections - face-api 的 WithFaceLandmarks 检测结果
 * @param {Object} [options]
 * @param {number} [options.speakerThreshold] - 判定为说话者的阈值，默认 SPEAKER_STANDARD_SCORE
 * @returns {{ facesData: FaceSpeechData[], speakingPerson: { index: number, box: Object, score: number, mouthOpenness: number } | null }}
 */
// const analyzeSpeaking = (detections, options = {}) => {
//     const { width = 0, height = 0 } = options;
//     const threshold = options.speakerThreshold ?? SPEAKER_STANDARD_SCORE;
//     const facesData = [];

//     for (let i = 0; i < detections.length; i++) {
//         const detection = detections[i];
//         const box = detection.detection.box;
//         const landmarks = detection.landmarks;
//         const mouthOpenness = calculateMouthOpenness(landmarks);
//         const score = mouthOpenness;
//         const isSpeaking = score > threshold;

//         facesData.push({
//             x: box.x / width,
//             y: box.y / height,
//             width: box.width / width,
//             height: box.height / height,
//             mouthOpenness,
//             isSpeaking,
//         });
//     }

//     return facesData;
// };
